// Web Audio synthesis engine for dopamine-hit sound effects.
// Two themes: "calm" (soft chimes, sine harps) and "house" (808 kicks, hi-hats, stabs).
// Zero external audio files.

let ctx = null;
let master = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.65;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

export function setMasterVolume(v) {
  ensureCtx();
  if (master) master.gain.value = Math.max(0, Math.min(1, v));
}

function envTone({ freq, dur = 0.18, type = "sine", peak = 0.35, attack = 0.005, release = 0.14, when = 0, detune = 0 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detune;
  osc.connect(g);
  g.connect(master);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + release);
  osc.start(t0);
  osc.stop(t0 + attack + release + 0.02);
}

function noiseBurst({ dur = 0.08, peak = 0.35, when = 0, hp = 2000, lp = 8000 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + when;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const hpF = c.createBiquadFilter();
  hpF.type = "highpass";
  hpF.frequency.value = hp;
  const lpF = c.createBiquadFilter();
  lpF.type = "lowpass";
  lpF.frequency.value = lp;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(hpF);
  hpF.connect(lpF);
  lpF.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

function kick808({ when = 0, peak = 0.6, freq = 120, drop = 45, dur = 0.35 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t0);
  osc.frequency.exponentialRampToValueAtTime(drop, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// ---------- Themes ----------
const NOTES = {
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  G5: 783.99,
  A5: 880,
  C6: 1046.5,
  E6: 1318.5,
  G6: 1568,
};

const themes = {
  calm: {
    start: () => {
      envTone({ freq: NOTES.G5, type: "sine", peak: 0.22, release: 0.35, when: 0 });
      envTone({ freq: NOTES.C6, type: "sine", peak: 0.18, release: 0.5, when: 0.08 });
    },
    crumb: () => envTone({ freq: NOTES.E6, type: "triangle", peak: 0.18, release: 0.1, when: 0 }),
    voice: () => {
      envTone({ freq: NOTES.C6, type: "sine", peak: 0.18, release: 0.15, when: 0 });
      envTone({ freq: NOTES.E6, type: "sine", peak: 0.15, release: 0.15, when: 0.06 });
      envTone({ freq: NOTES.G6, type: "sine", peak: 0.14, release: 0.2, when: 0.12 });
    },
    interrupt: () => {
      envTone({ freq: 320, type: "sine", peak: 0.32, release: 0.28, when: 0 });
      envTone({ freq: 220, type: "sine", peak: 0.28, release: 0.4, when: 0.06 });
    },
    tick: () => envTone({ freq: NOTES.A5, type: "sine", peak: 0.14, release: 0.12, when: 0 }),
    complete: () => {
      [NOTES.C6, NOTES.E6, NOTES.G6].forEach((f, i) =>
        envTone({ freq: f, type: "sine", peak: 0.22, release: 0.5, when: i * 0.14 }),
      );
      envTone({ freq: NOTES.C6 * 2, type: "sine", peak: 0.18, release: 0.9, when: 0.42 });
    },
  },
  house: {
    start: () => {
      kick808({ when: 0, peak: 0.55 });
      noiseBurst({ when: 0.15, dur: 0.08, peak: 0.3 });
      envTone({ freq: NOTES.A5, type: "sawtooth", peak: 0.25, release: 0.18, when: 0.3 });
    },
    crumb: () => noiseBurst({ dur: 0.04, peak: 0.28, hp: 6000, lp: 12000 }),
    voice: () => {
      envTone({ freq: NOTES.C6, type: "sawtooth", peak: 0.22, release: 0.12, when: 0 });
      noiseBurst({ when: 0.08, dur: 0.06, peak: 0.3, hp: 4000 });
    },
    interrupt: () => {
      kick808({ when: 0, peak: 0.75, freq: 160, drop: 40, dur: 0.5 });
      noiseBurst({ when: 0.05, dur: 0.12, peak: 0.35, hp: 3000 });
    },
    tick: () => noiseBurst({ dur: 0.03, peak: 0.22, hp: 8000 }),
    complete: () => {
      kick808({ when: 0, peak: 0.7 });
      kick808({ when: 0.25, peak: 0.7 });
      envTone({ freq: NOTES.C6, type: "sawtooth", peak: 0.28, release: 0.25, when: 0.5, detune: -6 });
      envTone({ freq: NOTES.G6, type: "sawtooth", peak: 0.28, release: 0.35, when: 0.5 });
      noiseBurst({ when: 0.5, dur: 0.2, peak: 0.32, hp: 4000 });
    },
  },
};

let currentTheme = "calm";
let muted = false;

export function setTheme(name) {
  currentTheme = themes[name] ? name : "calm";
}
export function getTheme() {
  return currentTheme;
}
export function setMuted(v) {
  muted = !!v;
}
export function isMuted() {
  return muted;
}

export function play(action) {
  if (muted) return;
  const bank = themes[currentTheme];
  if (bank && typeof bank[action] === "function") {
    try {
      bank[action]();
    } catch (e) {
      // audio may be locked until user gesture; ignore
    }
  }
}
